"use client";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Skeleton,
} from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  CircleDotIcon,
  ClipboardCopyIcon,
  LandmarkIcon,
  StoreIcon,
  WrenchIcon,
} from "lucide-react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type { AuditItem } from "@/modules/audit/presentation/api/client";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import { formatPrice } from "@/shared/lib/format-price";
import { resolveImageSrc } from "@/shared/ui/AppImage";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import {
  useApprovePayoutMutation,
  usePayoutAuditQuery,
  usePayoutDetailQuery,
  useProcessPayoutMutation,
} from "../api/payout-queries";

const STATUS_STYLE: Record<
  string,
  { dot: string; text: string; label: string }
> = {
  requested: {
    dot: "bg-primary",
    text: "text-primary",
    label: "ລໍຖ້າອະນຸມັດ",
  },
  approved: {
    dot: "bg-secondary",
    text: "text-secondary",
    label: "ອະນຸມັດແລ້ວ",
  },
  processing: {
    dot: "bg-primary",
    text: "text-primary",
    label: "ກຳລັງດຳເນີນການ",
  },
  paid: {
    dot: "bg-success",
    text: "text-success",
    label: "ຈ່າຍແລ້ວ",
  },
  failed: {
    dot: "bg-destructive",
    text: "text-destructive",
    label: "ລົ້ມເຫຼວ",
  },
  rejected: {
    dot: "bg-destructive",
    text: "text-destructive",
    label: "ປະຕິເສດ",
  },
};

const OWNER_TYPE_LABELS: Record<string, string> = {
  shop: "ຮ້ານຄ້າ",
  mechanic: "ຊ່າງ",
};

const METHOD_LABELS: Record<string, string> = {
  bank_transfer: "ໂອນທະນາຄານ",
  cash: "ເງິນສົດ",
};

function OwnerIcon({ type }: { type: string }) {
  switch (type) {
    case "shop":
      return <StoreIcon className="size-5 text-secondary" />;
    case "mechanic":
      return <WrenchIcon className="size-5 text-secondary" />;
    default:
      return <LandmarkIcon className="size-5 text-muted-foreground" />;
  }
}

export function PayoutDetailPage() {
  const { id } = useParams({ from: "/app/wallet/payouts/$id" });
  const nav = useNavigate();
  const { data: detail, isLoading } = usePayoutDetailQuery(id);
  const approveMutation = useApprovePayoutMutation(id);
  const processMutation = useProcessPayoutMutation(id);

  const headerBreadcrumb = (
    <Breadcrumb
      items={[
        { label: "ກະເປົາເງິນ & ການຈ່າຍເງິນ", to: "/app/wallet" },
        { label: "ການຈ່າຍເງິນອອກ", to: "/app/wallet/payouts" },
        {
          label: detail
            ? `#${detail.id.slice(0, 8).toUpperCase()}`
            : "ລາຍລະອຽດ",
        },
      ]}
    />
  );

  if (isLoading) {
    return (
      <>
        <Header>{headerBreadcrumb}</Header>
        <Main>
          <div className="space-y-4">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-[200px] rounded-xl" />
          </div>
        </Main>
      </>
    );
  }

  if (!detail) {
    return (
      <>
        <Header>{headerBreadcrumb}</Header>
        <Main>
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-destructive">
            ບໍ່ພົບຄຳຂໍຈ່າຍເງິນ ຫຼື ເກີດຂໍ້ຜິດພາດ
          </div>
        </Main>
      </>
    );
  }

  const statusStyle = STATUS_STYLE[detail.status] ?? {
    dot: "bg-muted-foreground",
    text: "text-muted-foreground",
    label: detail.status,
  };

  const canApprove = detail.status === "requested";
  const canProcess = detail.status === "approved";

  const STATUS_BADGE_VARIANT: Record<
    string,
    "default" | "secondary" | "outline" | "destructive"
  > = {
    requested: "default",
    approved: "secondary",
    processing: "default",
    paid: "outline",
    failed: "destructive",
    rejected: "destructive",
  };

  return (
    <>
      <Header>{headerBreadcrumb}</Header>

      <Main>
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="font-bold text-2xl tracking-tight">ຄຳຂໍຈ່າຍເງິນ</h2>
              <span className="font-mono text-muted-foreground text-sm">
                {detail.id}
              </span>
            </div>
            <div className="mt-1 flex items-center gap-2">
              <Badge variant={STATUS_BADGE_VARIANT[detail.status] ?? "outline"}>
                {statusStyle.label}
              </Badge>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => nav({ to: "/app/wallet/payouts" })}
            >
              <ArrowLeftIcon className="size-4" />
              ກັບຄືນ
            </Button>
            {canApprove && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button disabled={approveMutation.isPending}>
                    <CheckCircleIcon className="size-4" />
                    ອະນຸມັດ
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>ຢືນຢັນການອະນຸມັດ</AlertDialogTitle>
                    <AlertDialogDescription>
                      ທ່ານຕ້ອງການອະນຸມັດຄຳຂໍຈ່າຍເງິນນີ້ ຫຼືບໍ່?
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>ຍົກເລີກ</AlertDialogCancel>
                    <AlertDialogAction onClick={() => approveMutation.mutate()}>
                      ອະນຸມັດ
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            {canProcess && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button disabled={processMutation.isPending}>
                    <CheckCircleIcon className="size-4" />
                    ດຳເນີນການຈ່າຍ
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>ຢືນຢັນການດຳເນີນການ</AlertDialogTitle>
                    <AlertDialogDescription>
                      ຈະຫັກເງິນຈາກກະເປົາ ແລະ ອັບເດດສະຖານະເປັນ ຈ່າຍແລ້ວ. ດຳເນີນການບໍ່?
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>ຍົກເລີກ</AlertDialogCancel>
                    <AlertDialogAction onClick={() => processMutation.mutate()}>
                      ດຳເນີນການ
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>ຂໍ້ມູນຄຳຂໍ</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start gap-4">
                {detail.ownerImage ? (
                  <img
                    src={resolveImageSrc(detail.ownerImage) ?? ""}
                    alt={detail.ownerName ?? ""}
                    className="size-14 shrink-0 rounded-full object-cover"
                  />
                ) : (
                  <div className="flex size-14 shrink-0 items-center justify-center rounded-full bg-secondary/10">
                    <OwnerIcon type={detail.ownerType} />
                  </div>
                )}
                <div>
                  <p className="text-muted-foreground text-xs">ເຈົ້າຂອງກະເປົາ</p>
                  <p className="font-medium">
                    {detail.ownerName ?? "—"} (
                    {OWNER_TYPE_LABELS[detail.ownerType] ?? detail.ownerType})
                  </p>
                  {detail.ownerId && (
                    <p className="text-muted-foreground text-xs">
                      ID: {detail.ownerId.slice(0, 12)}...
                    </p>
                  )}
                </div>
              </div>

              <div>
                <p className="text-muted-foreground text-xs">ວັນທີສ້າງ</p>
                <p className="text-sm">
                  {formatDateTimeLocal(detail.createdAt)}
                </p>
              </div>

              <div className="grid gap-2 sm:grid-cols-2">
                <div>
                  <p className="text-muted-foreground text-xs">ວິທີຈ່າຍ</p>
                  <p className="text-sm">
                    {METHOD_LABELS[detail.payoutMethod] ?? detail.payoutMethod}
                  </p>
                </div>

                {detail.approvedByName && (
                  <div>
                    <p className="text-muted-foreground text-xs">ອະນຸມັດໂດຍ</p>
                    <p className="text-sm">{detail.approvedByName}</p>
                  </div>
                )}
              </div>

              {detail.payoutDestination &&
                Object.keys(detail.payoutDestination).length > 0 && (
                  <div>
                    <p className="text-muted-foreground text-xs">
                      ລາຍລະອຽດປາຍທາງ
                    </p>
                    <pre className="rounded bg-muted/50 p-2 text-xs">
                      {JSON.stringify(detail.payoutDestination, null, 2)}
                    </pre>
                  </div>
                )}
            </CardContent>
          </Card>

          <div className="space-y-4">
            <Card className="overflow-hidden rounded-xl border-primary/20 pt-0">
              <CardHeader className="border-b bg-primary/5 py-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <span className="inline-block size-2.5 rounded-full bg-primary" />
                    ສະຫຼຸບຍອດ
                  </CardTitle>
                  <span className="font-mono text-muted-foreground text-xs">
                    REF: {detail.id.slice(0, 12).toUpperCase()}
                  </span>
                </div>
              </CardHeader>
              <CardContent className="space-y-3 pt-4">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground text-sm">
                    ຍອດຈ່າຍ (Gross)
                  </span>
                  <span className="font-semibold tabular-nums">
                    {formatPrice(detail.amount, detail.currency)}
                  </span>
                </div>

                <div className="border-t" />

                <div className="flex items-center justify-between">
                  <span className="font-medium text-muted-foreground text-sm">
                    ຍອດສຸດທິ
                  </span>
                  <span className="font-bold text-2xl text-primary tabular-nums">
                    {formatPrice(detail.amount, detail.currency)}
                  </span>
                </div>

                <div className="flex items-center justify-between rounded-md bg-muted/50 px-3 py-2">
                  <span className="text-muted-foreground text-xs">
                    Wallet ID
                  </span>
                  <button
                    type="button"
                    className="flex items-center gap-1 font-mono text-xs transition-colors hover:text-primary"
                    onClick={() => {
                      navigator.clipboard.writeText(detail.walletId);
                    }}
                  >
                    {detail.walletId.slice(0, 12)}...
                    <ClipboardCopyIcon className="size-3" />
                  </button>
                </div>

                <div className="flex items-center justify-between rounded-md bg-muted/50 px-3 py-2">
                  <span className="text-muted-foreground text-xs">ສະຖານະ</span>
                  <Badge
                    variant={STATUS_BADGE_VARIANT[detail.status] ?? "outline"}
                  >
                    {statusStyle.label}
                  </Badge>
                </div>
              </CardContent>
            </Card>

            <AuditTimeline entityId={id} />
          </div>
        </div>
      </Main>
    </>
  );
}

// --- Audit Timeline ---

const ACTION_LABELS: Record<string, string> = {
  "payout.create": "ສ້າງຄຳຂໍຈ່າຍເງິນ",
  "payout.approve": "ອະນຸມັດການຈ່າຍເງິນ",
  "payout.process": "ດຳເນີນການຈ່າຍເງິນ",
};

const ACTION_COLORS: Record<string, { dot: string; line: string }> = {
  "payout.approve": { dot: "bg-success", line: "bg-success/30" },
  "payout.process": { dot: "bg-primary", line: "bg-primary/30" },
};

const DEFAULT_COLOR = { dot: "bg-secondary", line: "bg-border" };

function AuditTimeline({ entityId }: { entityId: string }) {
  const { data, isLoading } = usePayoutAuditQuery(entityId);

  if (isLoading) {
    return (
      <Card className="rounded-xl border-border">
        <CardHeader>
          <CardTitle className="text-lg">ບັນທຶກການດຳເນີນການ</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[1, 2].map((i) => (
              <div key={i} className="flex gap-3">
                <Skeleton className="size-6 shrink-0 rounded-full" />
                <div className="flex-1 space-y-1">
                  <Skeleton className="h-4 w-48" />
                  <Skeleton className="h-3 w-32" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const logs = data?.data ?? [];

  if (logs.length === 0) {
    return (
      <Card className="rounded-xl border-border">
        <CardHeader>
          <CardTitle className="text-lg">ບັນທຶກການດຳເນີນການ</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-sm">ຍັງບໍ່ມີບັນທຶກການດຳເນີນການ</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="rounded-xl border-border">
      <CardHeader>
        <CardTitle className="text-lg">ບັນທຶກການດຳເນີນການ</CardTitle>
      </CardHeader>
      <CardContent>
        <ol className="relative space-y-0">
          {logs.map((log, idx) => (
            <TimelineItem
              key={log.id}
              log={log}
              isLast={idx === logs.length - 1}
            />
          ))}
        </ol>
      </CardContent>
    </Card>
  );
}

function TimelineItem({ log, isLast }: { log: AuditItem; isLast: boolean }) {
  const colors = ACTION_COLORS[log.action] ?? DEFAULT_COLOR;
  const label = ACTION_LABELS[log.action] ?? log.action;

  return (
    <li className="relative flex gap-4 pb-6 last:pb-0">
      {!isLast && (
        <span
          className={`absolute top-6 left-[11px] h-[calc(100%-12px)] w-0.5 ${colors.line}`}
          aria-hidden
        />
      )}

      <div className="relative z-10 flex shrink-0 items-start pt-0.5">
        <CircleDotIcon
          className={`size-6 rounded-full p-0.5 text-white ${colors.dot}`}
        />
      </div>

      <div className="min-w-0 flex-1">
        <p className="font-medium text-sm">{label}</p>
        <p className="text-muted-foreground text-xs">
          {formatDateTimeLocal(log.occurredAt)}
        </p>
        {(log.actorName || log.actorId) && (
          <p className="mt-0.5 text-muted-foreground text-xs">
            ໂດຍ: {log.actorName ?? log.actorId}
          </p>
        )}
      </div>
    </li>
  );
}
